const NS = 'ProgressRadialWidget';
const CLS = 'progressRadialWidget';

let _isInteger = require('lodash/isInteger');
let _round = require('lodash/round');
import { easeInOutCubic } from 'easing-utils';

export class ProgressRadialWidget {
	constructor(element, initValue = 0) {
		this.element = element;
		this.$element = $(element);
		this.$legendText = this.$element.find(`.${CLS}-legend-text`);
		this.$legendPercentage = this.$element.find(`.${CLS}-legend-percentage`);

		this.devicePixelRatio = window.devicePixelRatio || 1;

		this.percentage = parseFloat(this.$element.attr('data-percentage')) || initValue;
		this.maxPercentage = 100;
		this.defaultRadius = 120;

		this.animationFrameId;
		this.isAnimating = false;

		// Variables sourced in .scss styles
		this.plainColor = '#e6e6e6';
		this.highlightColor = '#3c6';
		this.circleAnimationDuration = 300;
		this.arcAnimationFrameCount = 60;

		// Use the DOM element
		this.canvas = this.$element.find(`.${CLS}-canvas`)[0];

		if(this.percentage < 0 || this.percentage > this.maxPercentage) {
			this.percentage = 0;
			console.error(`${NS}::Error`, 'Please supply a valid range between 0–100');
		}

		this.drawCanvas();

		// Triggers an update event
		this.$element.on(`${CLS}.update`, _.throttle((event, params) => {
			let toPercentage = params && _isInteger(params.percentage) ? params.percentage : null;
			if(toPercentage !== null) {
				if(toPercentage >= 0 && toPercentage <= this.maxPercentage) {
					this.update(this.percentage, toPercentage, params.animate);
				}
				else {
					console.error(`${NS}::Error`, 'Please supply a valid range between 0–100');
				}
			}
			else {
				console.error(`${NS}::Error`, 'Please supply a valid integer value');
			}
		}, 500, { trailing: true }));

		// Deals with radius resizing.
		$(window).resize(_.debounce(() => {
			this.drawCanvas();
		}, 250));
	}

	drawCanvas() {
		this.canvas.width = this.canvas.getBoundingClientRect().width;
		this.canvas.height = this.canvas.width;

		// Adjust to the approprate ratio
		this.canvas.width *= this.devicePixelRatio;
		this.canvas.height *= this.devicePixelRatio;

		this.lineWidth = 8;
		this.context = this.canvas.getContext('2d');
		this.centerX = this.canvas.width / 2;
		this.centerY = this.canvas.height / 2;
		this.radius = (this.canvas.width - this.lineWidth) / 2;

		if(this.radius < 0) {
			this.radius = 0;
		}

		this.renderArc(this.percentage);
		this.renderLegend(this.percentage);
		this.rescaleLegend();
	}

	update(fromPercentage, toPercentage, animate = true) {
		if(fromPercentage === this.maxPercentage && toPercentage < this.maxPercentage) {
			// Fade out animation delay + another delay
			if(animate) {
				setTimeout(() => {
					this.animateArc(this.percentage, toPercentage);
				}, this.circleAnimationDuration + 50);
			}
			else {
				this.renderArc(toPercentage);
				this.renderLegend(toPercentage);
				this.percentage = toPercentage;
			}
		}
		else if(fromPercentage !== toPercentage) {
			if(animate) {
				this.animateArc(this.percentage, toPercentage);
			}
			else {
				this.renderArc(toPercentage);
				this.renderLegend(toPercentage);
				this.percentage = toPercentage;
			}
		}
	}

	animateArc(fromPercentage, toPercentage) {
		if(!this.isAnimating) {
			this.isAnimating = true;
			let shouldIncrement = fromPercentage < toPercentage;
			let range = Math.abs(fromPercentage - toPercentage); // Percentage range between currentPercentage and the percentage to set to

			let elapsedAnimationFrame = 0;

			let animate = () => {
				let easing = easeInOutCubic(elapsedAnimationFrame / this.arcAnimationFrameCount); // Set Easing based on animation interval

				let animatingPercentage;
				if(shouldIncrement) {
					animatingPercentage = ((range * easing) + fromPercentage);
				}
				else {
					animatingPercentage = Math.abs(((range * easing) - fromPercentage));
				}

				this.renderArc(animatingPercentage);
				this.renderLegend(animatingPercentage);
				this.percentage = Math.round(animatingPercentage);

				// Animation has completed
				if(elapsedAnimationFrame >= this.arcAnimationFrameCount) {
					cancelAnimationFrame(this.animationFrameId);
					this.isAnimating = false;
					this.percentage = toPercentage;
				}
				else {
					requestAnimationFrame(animate);
				}
				elapsedAnimationFrame++;
			};

			this.animationFrameId = requestAnimationFrame(animate);
		}
		else {
			if(this.animationFrameId) {
				cancelAnimationFrame(this.animationFrameId);
				this.isAnimating = false;
				this.animateArc(this.percentage, toPercentage);
			}
		}
	}

	renderArc(percentage) {
		let ctx = this.context;
		let circleStart = 1.5 * Math.PI;
		let circleEnd = circleStart + ((percentage / 100) * Math.PI * 2);

		ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

		ctx.beginPath();
		ctx.arc(this.centerX, this.centerY, this.radius, circleStart, 4 * Math.PI, false);
		ctx.lineWidth = this.lineWidth;
		ctx.strokeStyle = this.plainColor;
		ctx.stroke();

		if(percentage > 0) {
			ctx.beginPath();
			ctx.arc(this.centerX, this.centerY, this.radius, circleStart, circleEnd, false);
			ctx.lineWidth = this.lineWidth;
			ctx.lineCap = 'butt';
			ctx.strokeStyle = this.highlightColor;
			ctx.fillStyle = this.highlightColor;
			ctx.stroke();
		}
	}

	renderLegend(percentage) {
		this.$legendPercentage.html(`${_round(percentage, 0)}%`);
	}

	rescaleLegend() {
		if(this.$element.width() < this.defaultRadius) {
			this.$legendText.css({
				display: 'none'
			});
		}
		else {
			this.$legendText.css({
				display: 'block'
			});
		}
	}
}
